/**
 * Penyunting cepat yang terbuka dari kepala track lembar log.
 *
 * Warna, jenis garis dan kedua ujung skala sudah bisa disetel sejak lama — di
 * rel kanan, di dalam tab yang harus dibuka lebih dulu, pada panel track yang
 * harus dicari lebih dulu di antara lima panel lain. Itu tempat yang benar
 * untuk menyusun ulang sebuah lembar, dan tempat yang keliru untuk pekerjaan
 * yang sebenarnya sedang dilakukan: memandang sebuah kurva, memutuskan bahwa
 * skalanya terlalu lebar, dan membetulkannya tanpa memalingkan mata dari
 * kurvanya.
 *
 * Maka keduanya tetap ada. Yang di sini menyunting satu hal yang sedang
 * dipandang, lewat kotak kecil yang muncul di bawah benda yang diklik; yang di
 * rel menyunting lembarnya sebagai susunan. Keduanya menulis ke data yang sama,
 * jadi tidak ada yang bisa berselisih dengan yang lain.
 */
import { EditColoursButton } from "./PaletteEditor";
import { useEffect, useRef, type ReactNode } from "react";

import { PALETTE } from "../lib/correlation";
import { rampGradient, rampKeys, rampLabel } from "../lib/colorscale";
import {
  CURVE_STYLES,
  formatScaleValue,
  type CurveConfig,
  type CurveFill,
  type CurveStyle,
  type FillAnchor,
  type SpectrumConfig,
} from "../lib/tracklayout";

/**
 * Ketiga pilihan arsiran satu kurva, dinamai menurut ujung skala yang menjadi
 * garis dasarnya — bukan menurut sisi track.
 *
 * "Kiri" dan "kanan" berbohong pada sumbu yang dibalik, dan NPHI dan SW
 * memang lazim dibaca terbalik: pada track seperti itu ujung nilai tertinggi
 * berada di kiri, dan sebuah tombol bertuliskan "kanan" akan mengarsir sisi
 * yang berlawanan dengan yang diminta.
 */
const FILL_CHOICES: { value: FillAnchor | "none"; label: string; title: string }[] = [
  { value: "none", label: "None", title: "No shading" },
  { value: "min", label: "Min", title: "Shade from the curve to the low end of its scale" },
  { value: "max", label: "Max", title: "Shade from the curve to the high end of its scale" },
];

/**
 * Ke mana warna sebuah arsiran bergradasi mengikuti bacaannya.
 *
 * Dinamai menurut apa yang dilihat, bukan menurut medannya. `"across"` menyapu
 * ramp sepanjang lebar luasan, jadi yang menyatakan bacaan di sebuah kedalaman
 * adalah tepi luarnya — pinggir seluas satu-dua piksel. `"value"` mewarnai
 * seluruh lebar itu dengan satu warna, warna bacaannya, sehingga sebuah lapisan
 * tipis terbaca sebagai warna alih-alih sebagai lebar sepersekian milimeter.
 */
const FILL_BY_CHOICES: { value: "across" | "value"; label: string; title: string }[] = [
  {
    value: "value",
    label: "Value",
    title: "The whole width of the shading takes the colour of the reading at that depth",
  },
  {
    value: "across",
    label: "Width",
    title: "The colour sweeps the ramp from the baseline to the reading",
  },
];

/**
 * Kotak yang menggantung di bawah benda yang membukanya.
 *
 * Ia menutup pada klik di luar dirinya dan pada Escape. Keduanya perlu: kotak
 * yang hanya bisa ditutup lewat tombolnya sendiri menutupi kurva yang justru
 * sedang dibandingkan dengan hasil suntingannya.
 */
export function QuickPopover({
  onClose,
  align = "left",
  width = 176,
  children,
}: {
  onClose: () => void;
  /** Sisi mana yang disejajarkan dengan pembukanya. Track paling kanan
   *  menggantungkan kotaknya ke kiri supaya tidak keluar dari lembar. */
  align?: "left" | "right";
  width?: number;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const outside = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      // Klik di dalam kotak tidak boleh sampai ke kepala track di belakangnya,
      // yang akan menafsirkannya sebagai "pilih track ini".
      onClick={(event) => event.stopPropagation()}
      style={{
        position: "absolute",
        top: "100%",
        [align]: 0,
        marginTop: 3,
        width,
        zIndex: 30,
        display: "flex",
        flexDirection: "column",
        gap: 7,
        padding: 8,
        background: "var(--panel)",
        border: "1px solid var(--line-strong)",
        borderRadius: "var(--radius)",
        boxShadow: "var(--shadow-float)",
        cursor: "default",
      }}
    >
      {children}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
      <span style={{ font: "400 8.5px var(--font-mono)", color: "var(--ink-6)", letterSpacing: ".05em" }}>
        {label}
      </span>
      {children}
    </div>
  );
}

/**
 * Kotak angka sekecil mungkin yang masih bisa diketik.
 *
 * Nilainya dilaporkan saat kotak ditinggalkan atau Enter ditekan, bukan pada
 * setiap ketukan: melaporkan "1" di tengah jalan menuju "150" akan menggambar
 * ulang seluruh track pada skala yang tak seorang pun minta, dan pada sumbu
 * logaritmik juga sempat mengosongkannya.
 */
function ScaleInput({
  value,
  onCommit,
  title,
}: {
  value: number;
  onCommit: (value: number) => void;
  title: string;
}) {
  const ref = useRef<HTMLInputElement | null>(null);
  const commit = () => {
    const parsed = Number(ref.current?.value);
    if (Number.isFinite(parsed)) onCommit(parsed);
    else if (ref.current) ref.current.value = formatScaleValue(value);
  };
  return (
    <input
      ref={ref}
      // `key` pada nilainya membuat kotak ini lahir kembali ketika skalanya
      // berubah dari luar — tombol Auto, misalnya — tanpa perlu menyimpan
      // salinan draf yang harus disinkronkan sendiri.
      key={String(value)}
      defaultValue={formatScaleValue(value)}
      title={title}
      aria-label={title}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") commit();
        if (event.key === "Escape" && ref.current) ref.current.value = formatScaleValue(value);
      }}
      style={{
        height: 21,
        width: "100%",
        minWidth: 0,
        padding: "0 5px",
        background: "var(--panel-inset)",
        border: "1px solid var(--border-control)",
        borderRadius: 4,
        color: "var(--ink-2)",
        font: "400 10px var(--font-mono)",
      }}
    />
  );
}

/** Petak warna palet, dengan yang sedang terpakai bertepi tebal. */
function Swatches({ value, onPick }: { value: string; onPick: (colour: string) => void }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 3 }}>
      {PALETTE.map((colour) => (
        <button
          key={colour}
          type="button"
          aria-label={colour}
          title={colour}
          onClick={() => onPick(colour)}
          style={{
            height: 14,
            padding: 0,
            background: colour,
            border:
              colour.toLowerCase() === value.toLowerCase()
                ? "2px solid var(--ink)"
                : "1px solid var(--border-control)",
            borderRadius: 3,
            cursor: "pointer",
          }}
        />
      ))}
    </div>
  );
}

/**
 * Setelan satu kurva: warna, bentuk garis, ketebalan, dan kedua ujung skalanya.
 *
 * `min` dan `max` yang diterima adalah skala yang benar-benar dipakai
 * menggambar, bukan yang tersimpan — sebuah kurva tanpa setelan sendiri harus
 * memperlihatkan angka yang sedang berlaku baginya, sebab angka itulah yang
 * hendak diubah orang yang membuka kotak ini.
 */
export function CurveQuickEdit({
  name,
  color,
  style,
  width,
  min,
  max,
  custom,
  fill,
  onChange,
  onAuto,
  onClose,
  align,
}: {
  name: string;
  color: string;
  style: CurveStyle;
  /** Ketebalan yang sedang berlaku, termasuk ketebalan lembar. */
  width: number;
  min: number;
  max: number;
  /** Benar bila skalanya disetel sendiri; tombol Auto lalu hidup. */
  custom: boolean;
  /** Arsiran yang tersimpan pada kurva ini. Kosong berarti tanpa arsiran. */
  fill?: CurveFill;
  onChange: (patch: Partial<CurveConfig>) => void;
  onAuto: () => void;
  onClose: () => void;
  align?: "left" | "right";
}) {
  return (
    <QuickPopover onClose={onClose} align={align} width={200}>
      <span style={{ font: "600 10px var(--font-sans)", color: "var(--ink)" }}>{name}</span>

      <Row label="COLOUR">
        <Swatches value={color} onPick={(colour) => onChange({ color: colour })} />
      </Row>

      <Row label="LINE">
        <div style={{ display: "flex", gap: 3 }}>
          {CURVE_STYLES.map((option) => (
            <button
              key={option.value}
              type="button"
              title={option.label}
              aria-label={option.label}
              aria-pressed={option.value === style}
              onClick={() => onChange({ style: option.value })}
              style={{
                flex: 1,
                height: 20,
                display: "grid",
                placeItems: "center",
                padding: 0,
                background: option.value === style ? "var(--selected)" : "var(--control-flat)",
                border: `1px solid ${option.value === style ? "var(--accent)" : "var(--border-control)"}`,
                borderRadius: 4,
                cursor: "pointer",
              }}
            >
              {/* Contoh garisnya sendiri, bukan namanya: empat nama pada lebar
                  176 piksel tidak muat, dan bentuk garis memang lebih cepat
                  dikenali dari bentuknya. */}
              <span
                style={{
                  display: "block",
                  width: "70%",
                  height: 0,
                  borderTop: `2px ${
                    option.value === "dotted" ? "dotted" : option.value === "solid" ? "solid" : "dashed"
                  } ${color}`,
                }}
              />
            </button>
          ))}
        </div>
      </Row>

      <Row label={`WIDTH ${width.toFixed(1)} PX`}>
        <input
          type="range"
          min={0.6}
          max={4}
          step={0.1}
          value={width}
          aria-label={`Line width of ${name}`}
          onChange={(event) => onChange({ width: Number(event.target.value) })}
          style={{ width: "100%", accentColor: "var(--accent)" }}
        />
      </Row>

      <Row label="HORIZONTAL SCALE">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
          <ScaleInput value={min} title={`Left end of the ${name} scale`} onCommit={(v) => onChange({ min: v })} />
          <ScaleInput value={max} title={`Right end of the ${name} scale`} onCommit={(v) => onChange({ max: v })} />
        </div>
      </Row>

      <Row label="ARSIRAN">
        <div style={{ display: "flex", gap: 3 }}>
          {FILL_CHOICES.map((option) => {
            const on = (fill?.from ?? "none") === option.value;
            return (
              <button
                key={option.value}
                type="button"
                title={option.title}
                aria-pressed={on}
                onClick={() =>
                  onChange({
                    // Warna yang pernah dipilih dipulihkan apa adanya:
                    // mematikan arsiran sejenak untuk melihat kurva di
                    // bawahnya tidak boleh membuang warnanya.
                    fill:
                      option.value === "none"
                        ? undefined
                        : { ...fill, from: option.value },
                  })
                }
                style={{
                  flex: 1,
                  height: 20,
                  padding: 0,
                  background: on ? "var(--selected)" : "var(--control-flat)",
                  border: `1px solid ${on ? "var(--accent)" : "var(--border-control)"}`,
                  borderRadius: 4,
                  color: on ? "var(--ink-2)" : "var(--ink-4)",
                  font: "400 9px var(--font-mono)",
                  cursor: "pointer",
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </Row>

      {/* Warna arsiran hanya muncul ketika ada yang diarsir. Sederet petak
          warna yang tidak mengubah apa pun di layar adalah setelan yang
          mengajari penggunanya bahwa setelan di kotak ini tidak berlaku. */}
      {fill && (
        <>
          {/* Petak pertama adalah warna tunggal, dan ia sengaja duduk di
              deret yang sama dengan ramp: memilih gradasi dan kembali ke
              warna tunggal adalah satu keputusan, bukan dua tempat. */}
          <Row label="ARSIRAN GRADASI">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 3 }}>
              <button
                type="button"
                aria-label={`Shading of ${name} in one colour`}
                title="Single colour"
                onClick={() =>
                  onChange({ fill: { ...fill, ramp: undefined, reverse: undefined } })
                }
                style={{
                  height: 13,
                  padding: 0,
                  cursor: "pointer",
                  borderRadius: 3,
                  border: fill.ramp ? "1px solid var(--border-control)" : "2px solid var(--ink)",
                  background: fill.color ?? color,
                }}
              />
              {rampKeys().map((key) => (
                <button
                  key={key}
                  type="button"
                  aria-label={`Shading of ${name} graded by ${rampLabel(key)}`}
                  title={rampLabel(key)}
                  onClick={() =>
                    // Ramp yang baru dipilih mewarnai menurut bacaan tiap
                    // kedalaman — itu arti "bergradasi" bagi yang memintanya,
                    // dan itu pula yang dipakai log terarsir di volume 3D.
                    // Lembar yang sudah tersimpan tidak diubah: yang sudah
                    // punya `by` tetap sebagaimana disetel.
                    onChange({ fill: { ...fill, ramp: key, by: fill.by ?? "value" } })
                  }
                  style={{
                    height: 13,
                    padding: 0,
                    cursor: "pointer",
                    borderRadius: 3,
                    border:
                      key === fill.ramp ? "2px solid var(--ink)" : "1px solid var(--border-control)",
                    background: rampGradient(key, fill.reverse),
                  }}
                />
              ))}
            </div>
            {fill.ramp && (
              <EditColoursButton
                ramp={fill.ramp}
                onRamp={(key) => onChange({ fill: { ...fill, ramp: key } })}
              />
            )}
          </Row>

          {fill.ramp ? (
            <Row label="COLOUR FOLLOWS">
              <div style={{ display: "flex", gap: 3 }}>
                {FILL_BY_CHOICES.map((option) => {
                  const on = (fill.by ?? "across") === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      title={option.title}
                      aria-pressed={on}
                      aria-label={`${name} ${option.title}`}
                      onClick={() => onChange({ fill: { ...fill, by: option.value } })}
                      style={{
                        flex: 1,
                        height: 20,
                        padding: 0,
                        background: on ? "var(--selected)" : "var(--control-flat)",
                        border: `1px solid ${on ? "var(--accent)" : "var(--border-control)"}`,
                        borderRadius: 4,
                        color: on ? "var(--ink-2)" : "var(--ink-4)",
                        font: "400 9px var(--font-mono)",
                        cursor: "pointer",
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <QuickToggle
                on={fill.reverse === true}
                label="Balik ramp"
                onClick={() =>
                  onChange({ fill: { ...fill, reverse: fill.reverse ? undefined : true } })
                }
              />
            </Row>
          ) : (
            <Row label="SHADING COLOUR">
              <Swatches
                value={fill.color ?? color}
                onPick={(colour) => onChange({ fill: { ...fill, color: colour } })}
              />
              <QuickToggle
                on={fill.color === undefined}
                label="Follow the curve colour"
                onClick={() => onChange({ fill: { from: fill.from } })}
              />
            </Row>
          )}
        </>
      )}

      <div style={{ display: "flex", gap: 5 }}>
        <button
          type="button"
          onClick={onAuto}
          disabled={!custom}
          title="Back to the display scale saved on the curve"
          style={{
            flex: 1,
            height: 20,
            background: "transparent",
            border: "1px solid var(--border-control)",
            borderRadius: 4,
            color: custom ? "var(--ink-4)" : "var(--ink-7)",
            font: "400 9px var(--font-mono)",
            cursor: custom ? "pointer" : "default",
          }}
        >
          Auto
        </button>
        <button
          type="button"
          onClick={() => onChange({ min: max, max: min })}
          title="Flip the axis — NPHI and SW are read reversed"
          style={{
            flex: 1,
            height: 20,
            background: "transparent",
            border: "1px solid var(--border-control)",
            borderRadius: 4,
            color: "var(--ink-4)",
            font: "400 9px var(--font-mono)",
            cursor: "pointer",
          }}
        >
          Balik
        </button>
      </div>
    </QuickPopover>
  );
}

/**
 * Setelan pita warna sebuah track: ramp, arahnya, dan kedua ujung jepitannya.
 *
 * Jepitan diketik dalam satuan kurva, bukan digeser sebagai pecahan. Pita
 * permeabilitas dibaca pada dekade — 0,1 sampai 2000 — dan seorang
 * petrofisikawan yang ingin melihat batas 10 mD mengetik "10", bukan mencari
 * pecahan yang kebetulan jatuh di sana.
 */
export function SpectrumQuickEdit({
  curveName,
  ramp,
  reverse,
  bars,
  lo,
  hi,
  custom,
  onChange,
  onRemove,
  onClose,
  align,
}: {
  curveName: string;
  ramp: string;
  reverse: boolean;
  bars: boolean;
  /** Jepitan yang sedang berlaku, entah disetel sendiri atau warisan skala. */
  lo: number;
  hi: number;
  /** Benar bila jepitannya disetel sendiri; tombol jepitan auto lalu hidup. */
  custom: boolean;
  onChange: (patch: Partial<SpectrumConfig>) => void;
  onRemove: () => void;
  onClose: () => void;
  align?: "left" | "right";
}) {
  return (
    <QuickPopover onClose={onClose} align={align} width={200}>
      <span style={{ font: "600 10px var(--font-sans)", color: "var(--ink)" }}>
        Pita warna · {curveName}
      </span>

      <Row label="RAMP">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 3 }}>
          {rampKeys().map((key) => (
            <button
              key={key}
              type="button"
              aria-label={rampLabel(key)}
              title={rampLabel(key)}
              onClick={() => onChange({ ramp: key })}
              style={{
                height: 13,
                padding: 0,
                cursor: "pointer",
                borderRadius: 3,
                border: key === ramp ? "2px solid var(--ink)" : "1px solid var(--border-control)",
                background: rampGradient(key, reverse),
              }}
            />
          ))}
        </div>
        <EditColoursButton ramp={ramp} onRamp={(key) => onChange({ ramp: key })} />
      </Row>

      <Row label="COLOUR CLAMPS">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
          <ScaleInput
            value={lo}
            title={`Low end of the ${curveName} band`}
            onCommit={(v) => onChange({ min: v })}
          />
          <ScaleInput
            value={hi}
            title={`High end of the ${curveName} band`}
            onCommit={(v) => onChange({ max: v })}
          />
        </div>
      </Row>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
        <QuickToggle on={reverse} label="Balik ramp" onClick={() => onChange({ reverse: !reverse })} />
        <QuickToggle on={bars} label="Width follows value" onClick={() => onChange({ bars: !bars })} />
        <QuickToggle
          on={false}
          label="Jepitan auto"
          disabled={!custom}
          onClick={() => onChange({ min: undefined, max: undefined })}
        />
        <QuickToggle on={false} label="Remove band" danger onClick={onRemove} />
      </div>
    </QuickPopover>
  );
}

function QuickToggle({
  on,
  label,
  onClick,
  disabled,
  danger,
}: {
  on: boolean;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={on}
      style={{
        flex: "1 1 auto",
        height: 20,
        padding: "0 6px",
        background: on ? "var(--selected)" : "transparent",
        border: `1px solid ${on ? "var(--accent)" : "var(--border-control)"}`,
        borderRadius: 4,
        color: disabled ? "var(--ink-7)" : danger ? "var(--rose)" : on ? "var(--ink-2)" : "var(--ink-4)",
        font: "400 9px var(--font-mono)",
        whiteSpace: "nowrap",
        cursor: disabled ? "default" : "pointer",
      }}
    >
      {label}
    </button>
  );
}
